import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { StatusIndicator } from ".";
import { Icon } from "../../_components";
import { useApp, useTime, useMaps } from "../../_contexts";
import { ChronosDate } from "../../_helpers";
import { useEventListener } from "../../_hooks";
import { searchArticles } from "../../_services";

import { ResultList, FilterMenu } from "./";

export function GlobalSearch({ chronicle }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("default");
  const [results, setResults] = useState({});
  const [activeFilters, setActiveFilters] = useState([
    "events",
    "locations",
    "articles",
  ]);
  const [showFiltersMenu, setFiltersMenu] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const inputRef = useRef();

  const { items: events } = useSelector((state) => state.event);
  const { items: locations } = useSelector((state) => state.markers);
  const { details: world } = useSelector((state) => state.world);
  const { sidebarOpen, setSidebarOpen, openSidebar } = useApp();
  const {
    calendar,
    granularity,
    currentDate,
    unitsDisplay,
    setCurrentDate,
    updateScrollPosition,
  } = useTime();
  const { goTo, goToMap, getCoords, currentMap } = useMaps();

  const isDisabled = chronicle && chronicle.setupComplete ? false : true;

  const handleSearchChange = () => {
    if (inputRef && inputRef.current) setSearchTerm(inputRef.current.value);
  };

  const handleClear = () => {
    setSearchTerm("");
    setStatus("default");
    setShowResults(false);
  };

  const handleSidebarToggle = () => {
    if (isDisabled) return;
    setShowResults(false);
    if (!sidebarOpen) {
      openSidebar({
        type: "chronicle",
        payload: {},
      });
    } else {
      setSidebarOpen(false);
    }
    // console.log('Toggle!');
  };

  const handleFilterMenuToggle = () => {
    setShowResults(false);
    setFiltersMenu((prev) => !prev);
  };

  const handleFilterChange = (filter) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters((prev) => prev.filter((f) => f !== filter));
    } else {
      setActiveFilters((prev) => [...prev, filter]);
    }
  };

  const handleEntityClick = (type, item) => {
    if (type === "article") {
      setShowResults(false);
      return;
    }
    let payloadType;
    let actionType;
    switch (type) {
      case "location":
        actionType = "locationMarker";
        payloadType = "marker";
        break;
      default:
        actionType = type;
        payloadType = type;
        break;
    }
    if (type === "event") {
      const targetDate = new ChronosDate(item.fromDate, { calendar });
      const current = new ChronosDate(currentDate, { calendar });
      const segment = targetDate.getTimelineSegment(granularity, {
        unitCountDisplay: unitsDisplay,
      });
      const pos = targetDate.getPositionFromStart(segment.start, granularity);
      if (!current.isBetween(segment.start, segment.end)) {
        setCurrentDate(segment.start);
      }
      updateScrollPosition(pos);
      if (item.marker) {
        if (typeof item.marker === "string") {
          // console.log('Found Event with Marker!', item.marker);
          let location = locations.find((l) => l._id === item.marker);
          if (location) item.marker = location;
        }
      }
    }

    if (type === "location") {
      const currentCoords = getCoords();
      console.log("-----Current Map: ", currentMap, currentCoords, item);
      const currentMapID =
        typeof currentMap === "string" ? currentMap : currentMap._id;

      if (currentMapID !== item.parentMap) {
        goToMap(item.parentMap, { ...item.coords });
      } else {
        goTo({ ...item.coords });
      }
    }

    setShowResults(false);

    openSidebar({
      type: actionType,
      payload: { [payloadType]: item },
    });
  };

  useEffect(async () => {
    if (world && world.waID) {
      if (status === "loading") return;
      if (!searchTerm || searchTerm.length === 0) {
        setResults({});
        setShowResults(false);
        return;
      }

      setStatus("loading");

      let searchResults = await getResults(
        { events, locations },
        world.waID,
        searchTerm,
        {
          typesFilter: activeFilters,
        }
      );

      if (searchTerm && searchTerm.length > 0) {
        setStatus("search");
        setShowResults(true);
      } else {
        setStatus("default");
        setResults({});
      }
      setResults(searchResults);
      setShowResults(true);
    }
  }, [searchTerm]);

  useEventListener(
    "focus",
    () => {
      if (!showResults && hasResults(results)) {
        setShowResults(true);
      }
    },
    inputRef.current
  );
  // useEventListener(
  //   'blur',
  //   () => {
  //     setShowResults(false);
  //   },
  //   inputRef.current
  // );

  // useEffect(() => {

  // }, [inputRef]);

  return (
    <div className="chronos-global-search">
      <div className="chronos-global-search-bar">
        <button
          className={`chronos-sidebar-toggle btn btn-s${
            sidebarOpen && " active"
          }`}
          onClick={handleSidebarToggle}
        >
          {!sidebarOpen && <Icon icon="far fa-bars fa-fw" />}
          {sidebarOpen && <Icon icon="far fa-times fa-fw" />}
        </button>
        <input
          type="text"
          ref={inputRef}
          onChange={handleSearchChange}
          value={searchTerm}
          placeholder="Search Chronicle"
        />
        <StatusIndicator status={status} onClear={handleClear} />
        <button
          className="chronos-global-search-filters-toggle btn btn-s"
          onClick={handleFilterMenuToggle}
        >
          <Icon icon="fas fa-filter fa-fw" />
        </button>
        {showFiltersMenu && (
          <FilterMenu
            filters={activeFilters}
            onChange={handleFilterChange}
            isDisabled={isDisabled}
          />
        )}
      </div>
      {showResults && !isDisabled && (
        <ResultList
          results={results}
          isDisabled={isDisabled}
          onEntityClick={handleEntityClick}
        />
      )}
    </div>
  );
}

function hasResults(results) {
  const keys = Object.keys(results);
  if (!keys || keys.length === 0) {
    return false;
  }

  let hasResults = false;
  for (let key of keys) {
    if (results[key] && results[key].length > 0) {
      hasResults = true;
      break;
    }
  }

  return hasResults;
}

const defaultOptions = {
  typesFilter: [],
  excludeStubs: false,
  excludeWIP: false,
  excludeDrafts: false,
  excludePrivate: false,
};

function getResults(entities, worldID, searchTerm, options = defaultOptions) {
  return new Promise((resolve, reject) => {
    let searchResults = {
      events: [],
      locations: [],
      articles: [],
    };
    const searchString = searchTerm.toLocaleLowerCase();
    const { typesFilter: activeFilters } = options;
    if (entities !== undefined) {
      // Get Chronicle Events
      if (entities.events && entities.events.length > 0) {
        //...Events!
        if (activeFilters.includes("events")) {
          let eventResults =
            entities && entities.events
              ? entities.events
                  .map((item) => {
                    if (item.title.toLocaleLowerCase().includes(searchString)) {
                      item.matches = "Title";
                      return item;
                    }
                    if (item.category && item.category !== null) {
                      if (
                        item.category.name &&
                        item.category.name
                          .toLocaleLowerCase()
                          .includes(searchString)
                      ) {
                        item.matches = "Category";
                        return item;
                      }
                    }
                    if (item.tags && item.tags.length > 0) {
                      let tagFound = false;
                      for (let tag of item.tags) {
                        if (tag.toLocaleLowerCase().includes(searchString)) {
                          tagFound = tag;
                          break;
                        }
                      }
                      if (tagFound) {
                        item.matches = "Tag";
                        item.matchItem = tagFound;
                        return item;
                      }
                    }

                    if (
                      item.description &&
                      item.description
                        .toLocaleLowerCase()
                        .includes(searchString)
                    ) {
                      item.matches = "Description";
                      return item;
                    }

                    if (item.subGroups && item.subGroups.length > 0) {
                      let subGroupFound = false;
                      for (let group of item.subGroups) {
                        if (
                          group.name &&
                          group.name.toLocaleLowerCase().includes(searchString)
                        ) {
                          subGroupFound = group;
                          break;
                        }
                      }

                      if (subGroupFound) {
                        item.matches = "Subscriber Group";
                        item.matchItem = subGroupFound;
                        return item;
                      }
                      return;
                    }
                  })
                  .filter((i) => (i ? true : false))
                  .sort((a, b) => (a.matches > b.matches ? 1 : -1))
              : [];

          searchResults.events = eventResults;
        }
      }
      // Get Chronicle Locations
      if (entities.locations && entities.locations.length > 0) {
        ///...Locations!
        if (activeFilters.indexOf("locations") > -1) {
          let locationResults =
            entities && entities.locations
              ? entities.locations
                  .map((item) => {
                    if (item.markerType !== "location") {
                      return;
                    }
                    if (item.title.toLocaleLowerCase().includes(searchString)) {
                      item.matches = "Title";
                      return item;
                    }
                    if (item.tags && item.tags.length > 0) {
                      let tagFound = false;
                      for (let tag of item.tags) {
                        if (tag.toLocaleLowerCase().includes(searchString)) {
                          tagFound = tag;
                          break;
                        }
                      }
                      if (tagFound) {
                        item.matches = "Tag";
                        item.matchItem = tagFound;
                        return item;
                      }
                      if (
                        item.description &&
                        item.description
                          .toLocaleLowerCase()
                          .includes(searchString)
                      ) {
                        item.matches = "Description";
                        return item;
                      }
                      if (item.subGroups && item.subGroups.length > 0) {
                        let subGroupFound = false;
                        for (let group of item.subGroups) {
                          if (
                            group.name &&
                            group.name
                              .toLocaleLowerCase()
                              .includes(searchString)
                          ) {
                            subGroupFound = group;
                            break;
                          }
                        }
                        if (subGroupFound) {
                          item.matches = "Subscriber Group";
                          item.matchItem = subGroupFound;
                          return item;
                        }
                      }
                    }
                  })
                  .filter((i) => (i ? true : false))
                  .sort((a, b) => (a.matches > b.matches ? 1 : -1))
              : [];
          searchResults.locations = locationResults;
        }
      }
    }

    if (searchTerm && searchTerm.length > 2) {
      //...searchArticles
      searchArticles(searchTerm.toLocaleLowerCase(), worldID, options).then(
        (articles) => {
          if (!Array.isArray(articles)) {
            searchResults.articles = [];
            resolve(searchResults);
          } else {
            searchResults.articles = articles;
            resolve(searchResults);
          }
        }
      );
    } else {
      // Return the results
      resolve(searchResults);
    }
  });
}
